import React, { ComponentProps } from 'react';
import { TouchableOpacity } from 'react-native';

import { useHover } from '@hooks/useHover';

import RestyleIcon from './RestyleIcon';
import { Box } from '../../Restyle';

/*
  Example pre-defined size:
    <Icon name="Activity1" variant="s" color="magentaDark" />

  Example custom size:
    <Icon name="Activity1" color="magentaDark" width={30} height={30} />
*/
type Props = ComponentProps<typeof RestyleIcon> &
  Pick<
    ComponentProps<typeof TouchableOpacity>,
    'onPress' | 'onPressIn' | 'disabled' | 'onPressOut' | 'delayPressOut'
  > & {
    accessibilityLabel?: string;
    isWhiteIcon?: boolean;
    /** Hover tint override (TA-9005). Defaults to grey01; icons sitting on a
     * grey01 surface (e.g. the left rail) pass 'grey02' to stay visible. */
    hoverBackgroundColor?: 'grey01' | 'grey02';
  };

export const WebIcon = ({
  onPress,
  onPressIn,
  disabled,
  accessibilityLabel,
  onPressOut,
  delayPressOut,
  testId,
  isWhiteIcon = false,
  hoverBackgroundColor = 'grey01',
  ...rest
}: Props) => {
  // Ghost icon button (TA-9005): grey01 background on hover; a muted grey04
  // icon darkens to textPrimary. Colored icons (e.g. alertRed) keep their
  // colour — the darkening rule only applies to the muted ghost resting state.
  const { isHovered, hoverProps } = useHover({ disabled });
  const backgroundColor = isWhiteIcon ? undefined : hoverBackgroundColor;
  const iconColor =
    isHovered && rest.color === 'grey04' ? 'textPrimary' : rest.color;
  return (
    <>
      {isWhiteIcon && isHovered && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          backgroundColor='white'
          opacity={0.25}
          borderRadius='xs'
          padding='xxs'
        />
      )}

      <Box
        pointerEvents={onPress ? 'auto' : 'none'}
        {...hoverProps}
        backgroundColor={isHovered ? backgroundColor : undefined}
        borderRadius='xs'
        padding='xxs'>
        <TouchableOpacity
          testID={testId}
          disabled={disabled}
          onPressIn={onPressIn}
          onPress={(e) => {
            // Clear hover on press so the tint doesn't linger under an
            // opened menu/modal.
            hoverProps.onMouseLeave?.();
            onPress?.(e);
          }}
          onPressOut={onPressOut ?? undefined}
          delayPressOut={delayPressOut ?? 0}
          activeOpacity={(onPress || onPressIn) && !onPressOut ? undefined : 1}
          accessibilityLabel={accessibilityLabel}>
          <RestyleIcon {...rest} color={iconColor} />
        </TouchableOpacity>
      </Box>
    </>
  );
};
